import React, { useState, useEffect } from 'react';
import './App.css';
import VibeAuraBall from './VibeAuraBall';

function App() {
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    // Update date every minute
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000); // 60000 ms = 1 minute

    // Cleanup interval on component unmount
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="wsj-container">
      <header className="wsj-header">
        <div className="wsj-top-bar">
          <span className="wsj-lab">
            VVTF EMOTION LAB
            <VibeAuraBall />
          </span>
          <span className="wsj-date">
            {currentDate.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>
        <h1 className="wsj-title">VISUAL VIBE TRANSFORMER</h1>
        <p className="wsj-tagline">
          a new class of machine learning models that understand and predict emotion with visuals
        </p>
        <div className="wsj-properties">
          Properties
          <details className="wsj-property">
            <summary>Visual grounding</summary>
            <p className="wsj-property-explainer">
              Our model’s emotional predictions are grounded in pixel-level visual cues (faces, posture, motion, scene composition) rather than in language, labels, or semantic priors.
            </p>
          </details>
          <details className="wsj-property">
            <summary>Temporal dynamics</summary>
            <p className="wsj-property-explainer">
              Where a language model predicts the next discrete text token from a fixed vocabulary, our model outputs a continuous emotional state vector that reflects the moment-to-moment evolution of emotion in the visual sequence.
            </p>
          </details>
          <details className="wsj-property">
            <summary>Continuous geometry</summary>
            <p className="wsj-property-explainer">
              Our model represents emotion as a smooth, continuous vector in affective space, allowing fine-grained interpolation and precise affective trajectories rather than discrete categorical jumps.
            </p>
          </details>
        </div>
      </header>

      <section className="wsj-section">
        <p className="section-hint">PURPOSE</p>
        <h2 className="wsj-title">UNDERSTANDING AND PREDICTING EMOTION</h2>
        <p className="wsj-tagline">
          <span className="wsj-line">We build neural networks that do not rely on the semantic structure of language models.</span>
          <span className="wsj-line">Our models encode visual inputs as affective embeddings and learn the geometry of their affective dependencies.</span>
          <span className="wsj-line">We believe that the structure of emotion cannot be fully captured by the token geometry of an LLM.</span>
        </p>
      </section>

      <section className="wsj-section">
        <p className="section-hint">MISSION</p>
        <h2 className="wsj-title">HELPING MACHINES FEEL</h2>
        <p className="wsj-tagline">
          Human complexity goes beyond the rational. If we want to co‑exist and thrive alongside machines, we need to help them interpret our irrational side. To do so, we have to equip them with tools that go beyond rational intelligence.
        </p>
        <pre className="formula-snippet">human intelligence = rational intelligence (LLM) + emotional intelligence (VVTF)</pre>
      </section>

      <section className="wsj-section">
        <p className="section-hint">APPLICATIONS</p>
        <h2 className="wsj-title">INTRODUCING LUV.WTF - A PEOPLE MATCHING PLATFORM</h2>
        <p className="wsj-tagline">
          Our first VVTF model is trained on large‑scale psychology datasets that map visuals to valence–arousal–dominance (VAD) affective coordinates, drawing on 1M facial images from AffectNet and 10,000 short video clips from LIRIS‑ACCEDE.
        </p>
        <p className="wsj-tagline">
          Powered by VVTF, luv.wtf vibe-matches people for friendship and romance based on their visual choices.
        </p>
        <a
          className="explore-link explore-link--underlined"
          href="https://luv.wtf/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>Explore luv.wtf vibe matching</span>
          <svg className="explore-arrow" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.6 9.4 9.4 2.6" />
            <path d="M4.2 2.6h5.2v5.2" />
          </svg>
        </a>
        <h2 className="wsj-title">INTRODUCING VVTF.AI - CHAT EMOTIONS FOR CONVERSATIONAL AI</h2>
        <p className="wsj-tagline">
          We teach conversational AIs to use emojis, GIFs, and other affective visuals for a more human chatbot experience.
        </p>
        <a
          className="explore-link explore-link--underlined"
          href="https://vvtf.ai/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>Explore vvtf.ai</span>
          <svg className="explore-arrow" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.6 9.4 9.4 2.6" />
            <path d="M4.2 2.6h5.2v5.2" />
          </svg>
        </a>
      </section>

      <section className="wsj-section">
        <p className="section-hint">CONTACT</p>
        <h2 className="wsj-title">VISUAL VIBE TRANSFORMER</h2>
        <p className="wsj-tagline">
          a new class of machine learning models that understand and predict emotion with visuals
        </p>
        <a className="contact-button" href="mailto:contact@vvtf.ai">CONTACT</a>
      </section>
      
      <main className="wsj-content">
        <article className="wsj-article">
          <footer className="disclaimer">
            <p>VVTF EMOTION LAB © 2026</p>
          </footer>
        </article>
      </main>
    </div>
  );
}

export default App;
